import React, { useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import CgPopover from 'modules/Common/ui/CgPopover';
import { CgFilterSelect, CgFilterMultiSelect } from 'modules/Common/ui/CgFilterDropdown';
import {
  inputBaseClass,
  inputIdleClass,
  labelClass,
} from 'modules/Common/form/v2/primitives/inputClasses';
import { useTranslation } from 'react-i18next';

/**
 * CgColumnFilter is a component that provides inline filtering for table columns
 * using a popover interface.
 *
 * Supports filters that affect multiple columns through array columnKey values.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.filter - The filter definition object.
 * @param {string|Array<string>} props.filter.columnKey - Column key(s) this filter affects.
 * @param {any} props.value - The current filter value.
 * @param {Function} props.onChange - Callback function to handle filter changes.
 * @param {boolean} props.isActive - Whether the filter has an active value.
 * @returns {JSX.Element} The rendered column filter component.
 */
// One input field of a grouped column filter — same debounce/type handling
// as the single-filter path, with its label above the input.
const GroupFilterField = ({ filter, value, onChange }) => {
  const { t } = useTranslation('common');
  const [pendingValue, setPendingValue] = useState(null);

  useEffect(() => {
    if (pendingValue === null) return;
    const timer = setTimeout(() => {
      onChange(filter.name, pendingValue);
      setPendingValue(null);
    }, 1000);
    return () => clearTimeout(timer);
  }, [pendingValue, onChange, filter.name]);

  const handleChange = (newValue) => onChange(filter.name, newValue);

  const input = (() => {
    switch (filter.type) {
      case 'text':
      case 'number':
        return (
          <input
            type={filter.type}
            placeholder={t('common:columnFilter.filterColumnPlaceholder', {
              column: filter.label || t('common:columnFilter.columnFallback', 'column'),
              defaultValue: 'Filter {{column}}',
            })}
            value={pendingValue !== null ? pendingValue : value || ''}
            onChange={(e) => setPendingValue(e.target.value)}
            className={`${inputBaseClass} ${inputIdleClass}`}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={pendingValue !== null ? pendingValue : value || ''}
            onChange={(e) => setPendingValue(e.target.value)}
            className={`${inputBaseClass} ${inputIdleClass}`}
          />
        );
      case 'select':
        return (
          <CgFilterSelect
            options={filter.options || []}
            value={value ?? null}
            onChange={(val) => handleChange(val ?? '')}
            placeholder={t('common:columnFilter.filterColumnPlaceholder', {
              column: filter.label || t('common:columnFilter.columnFallback', 'column'),
              defaultValue: 'Filter {{column}}',
            })}
            isClearable
            filterOption={filter.filterOption}
            searchable={filter.searchable}
          />
        );
      case 'multi-select':
        return (
          <CgFilterMultiSelect
            options={filter.options || []}
            value={value ?? []}
            onChange={(vals) => handleChange(vals)}
            placeholder={t('common:columnFilter.filterColumnPlaceholder', {
              column: filter.label || t('common:columnFilter.columnFallback', 'column'),
              defaultValue: 'Filter {{column}}',
            })}
            isClearable
            filterOption={filter.filterOption}
            searchable={filter.searchable}
          />
        );
      default:
        return null;
    }
  })();

  return (
    <div className="w-[220px]">
      <div className={labelClass}>
        {filter.label || t('common:columnFilter.filterLabelFallback', 'Filter')}
      </div>
      {input}
    </div>
  );
};

// Several filters can target the same column (e.g. Supplier ID + Supplier
// name both live in the composite Name column). Instead of stacking one
// funnel icon per filter on the header, they share ONE icon whose popover
// offers every field.
const GroupColumnFilter = ({ filters, values, onChange }) => {
  const { t } = useTranslation('common');
  const hasValue = (f) => {
    const v = values?.[f.name];
    return v !== null && v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0);
  };
  const isActive = filters.some(hasValue);

  const handleClearAll = (e) => {
    e.stopPropagation();
    filters.forEach((f) => onChange(f.name, f.type === 'multi-select' ? [] : ''));
  };

  const popoverContent = (
    <div className="flex flex-col gap-3 p-1">
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={faIcons.faFilter}
            className="text-slate-400"
            size="sm"
          />
          <span className="text-sm font-semibold text-slate-800">
            {t('common:columnFilter.filterByMultiple', {
              columns: filters
                .map((f) => f.label || t('common:columnFilter.columnFallback', 'column'))
                .join(' or '),
              defaultValue: 'Filter by {{columns}}',
            })}
          </span>
        </div>
        {isActive && (
          <button
            onClick={handleClearAll}
            className="text-xs font-medium text-slate-500 hover:text-red-600 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-red-50 transition-colors"
            title={t('filters.clearFilters', 'Clear filters')}
          >
            <FontAwesomeIcon
              icon={faIcons.faTimes}
              size="xs"
            />
            {t('actions.clear', 'Clear')}
          </button>
        )}
      </div>
      {filters.map((f) => (
        <GroupFilterField
          key={f.name}
          filter={f}
          value={values?.[f.name]}
          onChange={onChange}
        />
      ))}
    </div>
  );

  return (
    <CgPopover
      triggerElement={
        <button
          className={`ml-1 p-1 rounded transition-colors ${
            isActive
              ? 'text-primaryColor hover:text-primaryColor/80'
              : 'text-gray-400 hover:text-gray-600'
          }`}
          title={t('common:columnFilter.filterByMultiple', {
            columns: filters
              .map((f) => f.label || t('common:columnFilter.columnFallback', 'column'))
              .join(' or '),
            defaultValue: 'Filter by {{columns}}',
          })}
        >
          <FontAwesomeIcon
            icon={isActive ? faIcons.faFilterCircleXmark : faIcons.faFilter}
            size="sm"
          />
        </button>
      }
      popoverContent={popoverContent}
      position="bottom"
      trigger="click"
      openDelay={0}
      closeDelay={100}
      sideOffset={8}
      popoverContentClassName="border border-gray-200 shadow-xl bg-white rounded-lg"
      maxWidth="max-w-[350px]"
    />
  );
};

const SingleColumnFilter = ({ filter, value, onChange, isActive = false }) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);
  // Some column-only filters arrive without a `label`. Fall back to a neutral
  // string so the user never sees literal "undefined" anywhere in the UI.
  const filterLabel = filter.label || t('common:columnFilter.columnFallback', 'column');

  // Debounce text input changes with 300ms delay
  useEffect(() => {
    if (pendingValue === null) return;

    const timer = setTimeout(() => {
      // Trigger the filter change with pending value
      onChange(filter.name, pendingValue);

      // Clear pending value
      setPendingValue(null);
    }, 1000);

    return () => clearTimeout(timer);
  }, [pendingValue, onChange, filter.name]);

  // Handle filter changes
  const handleFilterChange = useCallback(
    (newValue) => {
      onChange(filter.name, newValue);
    },
    [filter.name, onChange],
  );

  // Handle text input changes with debouncing
  const handleTextChange = useCallback((e) => {
    // For text inputs, use pending value to debounce
    setPendingValue(e.target.value);
  }, []);

  // Clear filter
  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      const clearValue = filter.type === 'multi-select' ? [] : '';
      handleFilterChange(clearValue);
      setIsOpen(false);
    },
    [filter.type, handleFilterChange],
  );

  // Render the filter input based on type
  const renderFilterInput = () => {
    const placeholder = filter.label
      ? t('common:columnFilter.filterColumnPlaceholder', {
          column: filterLabel,
          defaultValue: 'Filter {{column}}',
        })
      : t('common:columnFilter.filterLabelFallback', 'Filter');
    switch (filter.type) {
      case 'text':
      case 'number':
        return (
          <div className="w-[200px]">
            <input
              type={filter.type}
              placeholder={placeholder}
              value={pendingValue !== null ? pendingValue : value || ''}
              onChange={handleTextChange}
              className={`${inputBaseClass} ${inputIdleClass}`}
            />
          </div>
        );

      case 'date':
        return (
          <div className="w-[200px]">
            <input
              type="date"
              value={pendingValue !== null ? pendingValue : value || ''}
              onChange={handleTextChange}
              className={`${inputBaseClass} ${inputIdleClass}`}
            />
          </div>
        );

      case 'select':
        return (
          <div className="w-[200px]">
            <CgFilterSelect
              options={filter.options || []}
              value={value ?? null}
              onChange={(val) => handleFilterChange(val ?? '')}
              placeholder={placeholder}
              isClearable
              filterOption={filter.filterOption}
              searchable={filter.searchable}
            />
          </div>
        );

      case 'multi-select':
        return (
          <div className="w-[250px]">
            <CgFilterMultiSelect
              options={filter.options || []}
              value={value ?? []}
              onChange={(vals) => handleFilterChange(vals)}
              placeholder={placeholder}
              isClearable
              filterOption={filter.filterOption}
              searchable={filter.searchable}
            />
          </div>
        );

      default:
        return null;
    }
  };

  // Get display value for active filter
  const getDisplayValue = () => {
    // Use pending value for immediate UI updates, fallback to actual value
    const currentValue = pendingValue !== null ? pendingValue : value;

    if (
      currentValue === null ||
      currentValue === undefined ||
      currentValue === '' ||
      (Array.isArray(currentValue) && currentValue.length === 0)
    )
      return null;

    if (filter.type === 'select') {
      const option = filter.options?.find((opt) => opt.value === currentValue);
      return option ? option.label : currentValue;
    } else if (filter.type === 'multi-select' && Array.isArray(currentValue)) {
      if (currentValue.length === 1) {
        const option = filter.options?.find((opt) => opt.value === currentValue[0]);
        return option ? option.label : currentValue[0];
      }
      return t('common:filter.selectedCount', { count: currentValue.length });
    }
    return currentValue;
  };

  const displayValue = getDisplayValue();

  // Popover content
  const popoverContent = (
    <div className="flex flex-col gap-3 p-1">
      {/* Header with title and clear button */}
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={faIcons.faFilter}
            className="text-slate-400"
            size="sm"
          />
          <span className="text-sm font-semibold text-slate-800">
            {t('common:columnFilter.filterByMultiple', {
              columns: filterLabel,
              defaultValue: 'Filter by {{columns}}',
            })}
          </span>
        </div>
        {isActive && (
          <button
            onClick={handleClear}
            className="text-xs font-medium text-slate-500 hover:text-red-600 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-red-50 transition-colors"
            title={t('filters.clearFilter', 'Clear filter')}
          >
            <FontAwesomeIcon
              icon={faIcons.faTimes}
              size="xs"
            />
            {t('actions.clear', 'Clear')}
          </button>
        )}
      </div>

      {/* Current value display */}
      {isActive && (
        <div>
          <div className={labelClass}>{t('table.current', 'Current')}</div>
          <div className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full inline-flex items-center gap-2 text-xs max-w-full">
            <span className="font-medium text-primaryColor">{filterLabel}:</span>
            <CgPopover
              triggerElement={
                <span className="text-slate-600 truncate max-w-[100px]">{displayValue}</span>
              }
              popoverContent={
                <div className="flex items-center gap-2">
                  <span className="font-medium text-primaryColor">{filterLabel}:</span>
                  <div className="text-slate-700 text-sm">{displayValue}</div>
                </div>
              }
              position="bottom"
              trigger="hover"
              openDelay={0}
              closeDelay={100}
              sideOffset={8}
              popoverContentClassName="border border-slate-200 shadow-xl bg-white rounded-lg"
              maxWidth="max-w-[350px]"
            />
          </div>
        </div>
      )}
      {/* Filter input section */}
      <div>
        <div className={labelClass}>
          {filter.type === 'multi-select'
            ? t('common:columnFilter.selectOptions', 'Select options')
            : t('common:columnFilter.selectAnOption', 'Select an option')}
        </div>
        {renderFilterInput()}
      </div>
    </div>
  );

  return (
    <CgPopover
      triggerElement={
        <button
          className={`ml-1 p-1 rounded transition-colors ${
            isActive
              ? 'text-primaryColor hover:text-primaryColor/80'
              : 'text-gray-400 hover:text-gray-600'
          }`}
          title={
            isActive
              ? t('common:columnFilter.filteredByValue', {
                  value: displayValue,
                  defaultValue: 'Filtered by: {{value}}',
                })
              : t('common:columnFilter.filterColumnPlaceholder', {
                  column: filterLabel,
                  defaultValue: 'Filter {{column}}',
                })
          }
        >
          <FontAwesomeIcon
            icon={isActive ? faIcons.faFilterCircleXmark : faIcons.faFilter}
            size="sm"
          />
        </button>
      }
      popoverContent={popoverContent}
      position="bottom"
      trigger="click"
      openDelay={0}
      closeDelay={100}
      sideOffset={8}
      popoverContentClassName="border border-gray-200 shadow-xl bg-white rounded-lg"
      maxWidth="max-w-[350px]"
    />
  );
};

// Hook-free dispatcher: one icon per column. Multiple filters targeting
// the column render the grouped popover; a single filter keeps the
// original behavior.
const CgColumnFilter = ({ filter, filters, value, values, onChange, isActive = false }) => {
  if (Array.isArray(filters) && filters.length > 1) {
    return (
      <GroupColumnFilter
        filters={filters}
        values={values}
        onChange={onChange}
      />
    );
  }
  const single = filter || (Array.isArray(filters) ? filters[0] : null);
  if (!single) return null;
  return (
    <SingleColumnFilter
      filter={single}
      value={value !== undefined ? value : values?.[single.name]}
      onChange={onChange}
      isActive={isActive}
    />
  );
};

export default CgColumnFilter;
